import { Link, useNavigate } from "react-router-dom";
import { Badge, Card, Container, Form, Row, Table } from "react-bootstrap";
import InputButton from "../../../components/form-elements/Button";
import Input from "../../../components/form-elements/Input";
import { SubmitHandler, useForm } from "react-hook-form";
import Select from "../../../components/form-elements/Select";
import { useState } from "react";
import DateRangePickerCustom from "../../../components/form-elements/DateRangePicker";
import useReservations, {
  IReservation,
  IReservationsParams,
} from "./useReservations";
import Pagination from "../../../components/pagination";
import { TableLoader } from "../../../components/Loaders";
import useUserPermissions from "../../../hooks/useUserPermissions";
import CancelBooking from "./cancel/CancelBooking";
import Fetch from "../../../utils/fetch";
import SelectAsync from "../../../components/form-elements/AsyncSelect";

interface IFormValues {
  date_type: string;
  property_id: string;
  reservation_id: string;
  reservation_status: string;
  guest_name: string;
  guest_email: string;
  guest_mobile_no: string;
  ota_tracking_id: string;
}

export default function ManageBooking() {
  const navigate = useNavigate();
  const [fromDate, setFromDate] = useState<any>(null);
  const [toDate, setToDate] = useState<any>(null);

  const { control, handleSubmit } = useForm<IFormValues>({
    defaultValues: {
      date_type: "",
      property_id: "",
      reservation_id: "",
      reservation_status: "",
      guest_name: "",
      guest_email: "",
      guest_mobile_no: "",
      ota_tracking_id: "",
    },
  });
  const [offset, setOffset] = useState(0);
  const [pageSize] = useState(10);
  const [activePage, setActivePage] = useState(1);

  const [formData, setFormData] = useState<IReservationsParams>({
    date_type: "",
    from_date: fromDate,
    to_date: toDate,
    property_id: "",
    reservation_id: "",
    reservation_status: "",
    guest_name: "",
    guest_email: "",
    guest_mobile_no: "",
    ota_tracking_id: "",
    offset,
    pageSize,
  });

  const permissions = useUserPermissions();

  const reservations = useReservations(formData);

  const handlePageClick = (pageNumber: number) => {
    setActivePage(pageNumber);
    setOffset(pageSize * (pageNumber - 1));
    setFormData({
      ...formData,
      offset: pageSize * (pageNumber - 1),
    });
  };

  const handleFilter: SubmitHandler<IFormValues> = (data) => {
    const property_id =
      data.property_id === "" || data.property_id === null
        ? ""
        : Object(data.property_id).value;

    setOffset(0);
    setFormData({
      ...data,
      property_id,
      pageSize,
      offset: 0,
      from_date: fromDate,
      to_date: toDate,
    });
    setActivePage(1);
  };

  const loadOptions = (inputValue: string) => {
    return new Promise<any>((resolve) => {
      resolve(searchProperties(inputValue));
    });
  };

  const searchProperties = async (inputValue: string) => {
    if (inputValue.length > 0) {
      const recommendation = await Fetch({
        method: "POST",
        url: `${process.env.REACT_APP_BACKEND_URL}/api/b2b/dropdowns/search-properties`,
        body: { name: inputValue },
        navigate,
      });
      return recommendation.data;
    } else {
      return [];
    }
  };

  return (
    <Container>
      <Card className="p-3 mt-2 mt-md-3 mt-lg-4">
        <Form className="form-horizontal" onSubmit={handleSubmit(handleFilter)}>
          <Row className="mt-0 align-items-end">
            <Select
              xs={12}
              sm={4}
              lg={2}
              size="sm"
              label="Date Of"
              name="date_type"
              className="mb-3"
              control={control}
              selectOption="Select One"
              options={[
                { id: "check_in", name: "Check In" },
                { id: "check_out", name: "Check Out" },
                { id: "reservation", name: "Reservation" },
              ]}
            />

            <DateRangePickerCustom
              sm={8}
              lg={4}
              size="sm"
              startDateLabel="Date From"
              endDateLabel="Date To"
              endDatePlaceholders="dd/mm/yyyy"
              startDatePlaceholders="dd/mm/yyyy"
              minimumLength={0}
              startDate={fromDate}
              endDate={toDate}
              onStartDateChange={(date) => setFromDate(date!)}
              onEndDateChange={(date) => setToDate(date!)}
            />

            <SelectAsync
              xs={6}
              sm={4}
              group_class="mb-3"
              control={control}
              name="property_id"
              label="Property"
              placeholder="Search Property"
              loadOptions={loadOptions}
              styles={{
                control: (provided: any) => ({
                  ...provided,
                  minHeight: "calc(1.5em + .5rem + 2px)",
                  height: "calc(1.5em + .5rem + 2px)",
                }),
                input: (provided: any) => ({
                  ...provided,
                  margin: "0px",
                  padding: "0px",
                  lineHeight: "1.5",
                }),
                dropdownIndicator: (provided: any) => ({
                  ...provided,
                  padding: "0.290rem 0.75rem",
                }),
              }}
            />
            <Select
              xs={6}
              sm={4}
              lg={2}
              size="sm"
              label="Reservation Status"
              name="reservation_status"
              className="mb-3"
              control={control}
              selectOption="Select One"
              options={[
                { id: "confirmed", name: "Confirmed" },
                { id: "canceled", name: "Canceled" },
              ]}
            />

            <Input
              xs={6}
              sm={4}
              lg={2}
              size="sm"
              label="Reservation ID"
              name="reservation_id"
              type="text"
              className="mb-3"
              aria-autocomplete="none"
              control={control}
              placeholder="Reservation id"
            />

            <Input
              xs={6}
              sm={4}
              lg={2}
              size="sm"
              label="Guest Name"
              name="guest_name"
              type="text"
              className="mb-3"
              aria-autocomplete="none"
              control={control}
              placeholder="Guest Name"
            />

            <Input
              xs={6}
              sm={4}
              lg={2}
              size="sm"
              label="Guest Phone"
              name="guest_mobile_no"
              type="text"
              className="mb-3"
              aria-autocomplete="none"
              control={control}
              placeholder="Guest Phone"
            />

            <Input
              xs={6}
              sm={4}
              lg={2}
              size="sm"
              label="Guest Email"
              name="guest_email"
              type="text"
              className="mb-3"
              aria-autocomplete="none"
              control={control}
              placeholder="Guest Email"
            />
            <Input
              xs={6}
              sm={4}
              lg={2}
              size="sm"
              type="text"
              group_class="mb-3"
              name="ota_tracking_id"
              control={control}
              label="B2B Confirmation ID"
              placeholder="Client Tracking Id"
              className="highlight-input-bg"
            />
            <InputButton
              xs={6}
              sm={4}
              lg={2}
              label="Submit"
              type="submit"
              variant="primary"
              className="w-100 mb-3"
            />
            {/* <InputButton
              xs={6}
              sm={4}
              lg={2}
              size="sm"
              variant="primary"
              className="mb-3"
              icon={<FontAwesomeIcon icon={faRecycle} />}
              onClick={() => {
                setFromDate(null);
                setToDate(null);
                reset();
              }}
            /> */}
          </Row>
        </Form>
      </Card>
      <Card className="mt-4 mt-2 mt-md-3 mt-lg-4 p-2 font_14">
        <div className="d-flex justify-content-between px-2 py-3 align-items-center">
          <h5 className="fw-bold mb-0">Manage Booking</h5>
          {/* <div className="d-flex align-items-center">
              <button className="border-0 bg-white btn_link_effect">
                <FontAwesomeIcon icon={faFileExcel} className="fs-4" />
              </button>
              <button className="mx-1 mx-sm-2 border-0 bg-white">
                <img
                  src={addMoneyIcon}
                  alt="add_money_icon"
                  className="h-25 add_money_icon"
                />
              </button>
            </div> */}
        </div>
        <Table
          responsive="lg"
          className="caption-top"
          bordered
          striped={!reservations.isLoading}
          hover={!reservations.isLoading}
        >
          <thead className="border-bottom">
            <tr>
              <th title="Reservation ID">Res. ID</th>
              <th>Hotel Name</th>
              <th>Guest</th>
              <th>Check In/Out</th>
              <th title="Number of Nights">Nts</th>
              <th title="Number of Rooms">Rms</th>
              <th>Booked On</th>
              <th>Status</th>
              <th>Amount</th>
              <th title="Hotel Confirmation ID">Hotel Conf. ID</th>
              <th title="B2B Confirmation ID">B2B Conf. ID</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {reservations.isLoading ? (
              <TableLoader columnLength={10} />
            ) : reservations.isSuccess ? (
              <>
                {reservations.data.data.reservations.map(
                  (reservation: IReservation) => {
                    return (
                      <tr className="my-1" key={reservation.id}>
                        <td>
                          <span className="text-primary fw-bold">
                            {reservation.id}
                          </span>
                        </td>
                        <td>{reservation.property_name}</td>
                        <td>{reservation.guest_name}</td>
                        <td>
                          {reservation.check_in}
                          <br />
                          {reservation.check_out}
                        </td>

                        <td>{reservation.number_of_nights}</td>
                        <td>{reservation.rooms}</td>
                        <td>{reservation.created_at}</td>
                        <td>
                          <Badge
                            bg={generateStatusBadge(
                              reservation.reservation_status
                            )}
                          >
                            {reservation.reservation_status}
                          </Badge>
                        </td>
                        <td>
                          {reservation.receive_currency &&
                          reservation.receive_amount
                            ? `${reservation.receive_amount} ${reservation.receive_currency}`
                            : `${Number(reservation.grand_total_rate).toFixed(
                                2
                              )} BDT`}
                        </td>
                        <td>{reservation.property_tracking_id}</td>
                        <td>{reservation.ota_tracking_id}</td>
                        <td>
                          {reservation.is_cancelable &&
                          reservation.reservation_status === "confirmed" &&
                          permissions.data?.data.includes(
                            "b2b_cancel_property_booking"
                          ) &&
                          !reservation.is_invoice_closed ? (
                            <CancelBooking reservation={reservation} />
                          ) : null}
                          {reservation.receive_currency === "USD" &&
                          reservation.receive_amount ? (
                            <Link
                              to={`/v2/manage/booking/details/${reservation.id}`}
                            >
                              <span className="text-primary">View</span>
                            </Link>
                          ) : (
                            <Link
                              to={`/manage/booking/details/${reservation.id}`}
                            >
                              <span className="text-primary">View</span>
                            </Link>
                          )}
                        </td>
                      </tr>
                    );
                  }
                )}
              </>
            ) : (
              <h4 className="teaxt-center">Data not found!</h4>
            )}
          </tbody>
        </Table>

        {reservations.isSuccess && (
          <Pagination
            count={Number(reservations.data.data.count[0].count)}
            handlePageClick={handlePageClick}
            pageSize={pageSize}
            activePage={activePage}
            name="reservation"
          />
        )}
      </Card>
    </Container>
  );
}
// const getPaymentStatusColor = (status: string) => {
//   let color = "text-dark";
//   switch (status) {
//     case "PAID":
//       color = "text-success";
//       break;
//     case "PARTIAL PAID":
//       color = "text-warning";
//       break;
//     case "UNPAID":
//       color = "text-danger";
//       break;
//   }
//   return color;
// };
const generateStatusBadge = (status: string) => {
  let status_badge = "badge";
  status === "confirmed"
    ? (status_badge += " bg-success")
    : (status_badge += " bg-danger");

  return status_badge;
};
